import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Code, Copy, Check, AlertCircle } from 'lucide-react';
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  AlertDescription,
  Skeleton,
  Label,
  Textarea,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@aaas/shared';
import { useWidgetQuery, useDeleteWidgetMutation, useWidgetEmbedCodeQuery } from '../hooks';

const WidgetDetailPage = () => {
  const navigate = useNavigate();
  const { widgetId } = useParams<{ widgetId: string }>();
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    data: widget,
    isLoading,
    error: fetchError,
  } = useWidgetQuery({
    widgetId,
  });

  const {
    data: embedCode,
    isLoading: embedLoading,
    error: embedError,
  } = useWidgetEmbedCodeQuery({
    widgetId,
    spaceId: selectedSpaceId,
    enabled: !!selectedSpaceId && embedDialogOpen,
  });

  const deleteMutation = useDeleteWidgetMutation();

  const handleDelete = async () => {
    if (!widgetId) return;

    try {
      await deleteMutation.mutateAsync(widgetId);
      navigate('/kms/widgets');
    } catch (error) {
      console.error('Failed to delete widget:', error);
    }
  };

  const handleGenerateEmbed = (spaceId: string) => {
    setSelectedSpaceId(spaceId);
    setEmbedDialogOpen(true);
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton variant="rectangular" className="w-10 h-10" />
          <Skeleton variant="text" className="w-64 h-8" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton variant="rectangular" className="w-full h-96" />
          <Skeleton variant="rectangular" className="w-full h-96" />
        </div>
      </div>
    );
  }

  if (fetchError || !widget) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/kms/widgets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">Widget Details</h1>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {fetchError instanceof Error ? fetchError.message : 'Widget not found'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/kms/widgets')}>Back to Widgets</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/kms/widgets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{widget.name}</h1>
              <Badge variant={widget.is_active ? 'default' : 'secondary'}>
                {widget.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            {widget.description && <p className="text-gray-600 mt-1">{widget.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/kms/widgets/${widgetId}/edit`)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Widget Configuration */}
        <div className="space-y-6">
          {/* Appearance */}
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Theme Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div
                      className="w-6 h-6 rounded border"
                      style={{ backgroundColor: widget.theme_color }}
                    />
                    <span className="text-sm">{widget.theme_color}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Accent Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div
                      className="w-6 h-6 rounded border"
                      style={{ backgroundColor: widget.accent_color }}
                    />
                    <span className="text-sm">{widget.accent_color}</span>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Position</Label>
                <p className="text-sm mt-1">{widget.position}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Size</Label>
                <p className="text-sm mt-1">
                  {widget.widget_size} ({widget.widget_height})
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Header Title</Label>
                <p className="text-sm mt-1">{widget.header_title}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Bot Name</Label>
                <p className="text-sm mt-1">{widget.bot_name}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Avatar</Label>
                <p className="text-sm mt-1">{widget.avatar_icon}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Show Powered By</Label>
                <Badge variant={widget.show_powered_by ? 'default' : 'secondary'}>
                  {widget.show_powered_by ? 'Yes' : 'No'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Behavior */}
          <Card>
            <CardHeader>
              <CardTitle>Behavior</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Auto Open</Label>
                <p className="text-sm mt-1">
                  {widget.auto_open ? `Yes (after ${widget.auto_open_delay}ms)` : 'No'}
                </p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Persist Conversation</Label>
                <Badge variant={widget.persist_conversation ? 'default' : 'secondary'}>
                  {widget.persist_conversation ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Max Message Length</Label>
                <p className="text-sm mt-1">{widget.max_message_length} characters</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Spaces & Embed Codes */}
        <div className="space-y-6">
          {/* Associated Spaces */}
          <Card>
            <CardHeader>
              <CardTitle>Associated Spaces ({widget.spaces?.length ?? 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {(widget.spaces?.length ?? 0) === 0 ? (
                <p className="text-sm text-gray-500">No spaces associated</p>
              ) : (
                <div className="space-y-2">
                  {widget.spaces?.map((space) => (
                    <div
                      key={space.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div>
                        <p className="font-medium">{space.name}</p>
                        {space.description && (
                          <p className="text-sm text-gray-500">{space.description}</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerateEmbed(space.id)}
                      >
                        <Code className="w-4 h-4 mr-2" />
                        Embed Code
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Messaging */}
          <Card>
            <CardHeader>
              <CardTitle>Messaging</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Welcome Message</Label>
                <p className="text-sm mt-1">{widget.welcome_message}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Input Placeholder</Label>
                <p className="text-sm mt-1">{widget.input_placeholder}</p>
              </div>
              {widget.suggested_questions && widget.suggested_questions.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-500">Suggested Questions</Label>
                  <ul className="list-disc list-inside text-sm mt-1 space-y-1">
                    {widget.suggested_questions.map((q, idx) => (
                      <li key={idx}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Security */}
          <Card>
            <CardHeader>
              <CardTitle>Security</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Allowed Domains</Label>
                <p className="text-sm mt-1">
                  {widget.allowed_domains && widget.allowed_domains.length > 0
                    ? widget.allowed_domains.join(', ')
                    : 'All domains allowed'}
                </p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Rate Limits</Label>
                <p className="text-sm mt-1">
                  {widget.rate_limit_per_minute}/min, {widget.rate_limit_per_hour}/hour
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Widget</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{widget.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed Code Dialog */}
      <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Embed Code</DialogTitle>
            <DialogDescription>
              Copy and paste this code into your website to add the chat widget.
            </DialogDescription>
          </DialogHeader>

          {embedLoading ? (
            <div className="space-y-4">
              <Skeleton variant="rectangular" className="w-full h-32" />
              <Skeleton variant="rectangular" className="w-full h-32" />
            </div>
          ) : embedError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Failed to generate embed code</AlertDescription>
            </Alert>
          ) : embedCode ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Embed Code</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(embedCode.embed_code, 'full')}
                  >
                    {copiedField === 'full' ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  value={embedCode.embed_code}
                  readOnly
                  rows={10}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-gray-500">
                  Copy and paste this code into your website's HTML, just before the closing{' '}
                  <code>&lt;/body&gt;</code> tag.
                </p>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button onClick={() => setEmbedDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WidgetDetailPage;
